import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NEPALI_MAP } from "../lib/nepaliKeyboard";
import Keyboard from "./Keyboard";
import { RotateCcw } from "lucide-react";

/**
 * Core typing engine. Handles keydown events and character comparison.
 * Nepali mode intercepts the physical key and maps to Devanagari.
 *
 * Props:
 *  - text: target string
 *  - language: "english" | "nepali"
 *  - mode: "practice" | "test" | "lesson"
 *  - durationSeconds: if set, timed test that ends when time is up
 *  - onComplete: ({ wpm, accuracy, correct, incorrect, total, durationSeconds }) => void
 */
export default function TypingEngine({
    text,
    language,
    mode = "practice",
    durationSeconds = null,
    onComplete,
}) {
    const [typed, setTyped] = useState("");
    const [errors, setErrors] = useState(0);
    const [activeCode, setActiveCode] = useState(null);
    const [startedAt, setStartedAt] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [finished, setFinished] = useState(false);
    const containerRef = useRef(null);

    const chars = useMemo(() => Array.from(text), [text]);
    const typedChars = useMemo(() => Array.from(typed), [typed]);

    const complete = useCallback(
        (final = false) => {
            if (finished) return;
            setFinished(true);
            const elapsed = startedAt ? (Date.now() - startedAt) / 1000 : 0;
            const totalTyped = typedChars.length;
            const correct = typedChars.filter((c, i) => c === chars[i]).length;
            const incorrect = totalTyped - correct;
            const wordsTyped = correct / 5;
            const minutes = elapsed / 60;
            const wpm = minutes > 0 ? wordsTyped / minutes : 0;
            const accuracy = totalTyped > 0 ? (correct / totalTyped) * 100 : 0;
            onComplete?.({
                wpm: Math.max(0, wpm),
                accuracy: Math.max(0, Math.min(100, accuracy)),
                correct,
                incorrect,
                total: totalTyped,
                durationSeconds: Math.round(elapsed),
                finalized: final,
            });
        },
        [startedAt, typedChars, chars, onComplete, finished]
    );

    const handleKeyDown = useCallback(
        (e) => {
            if (finished) return;

            // Prevent tab/space/backspace from scrolling / navigating
            if (["Space", "Backspace", "Tab"].includes(e.code)) e.preventDefault();

            setActiveCode(e.code);

            if (e.code === "Backspace") {
                setTyped((t) => Array.from(t).slice(0, -1).join(""));
                return;
            }

            // Determine the character produced by this keystroke
            let ch = null;
            if (language === "nepali") {
                const map = NEPALI_MAP[e.code];
                if (map) {
                    ch = e.shiftKey ? map.shift : map.normal;
                } else if (e.key.length === 1) {
                    // fallback for punctuation not in map
                    ch = e.key;
                }
            } else {
                if (e.key.length === 1) ch = e.key;
                else if (e.code === "Space") ch = " ";
            }

            if (!ch) return;

            // Start the timer on the first valid keystroke
            setStartedAt((s) => s ?? Date.now());

            setTyped((prev) => {
                const nextIndex = Array.from(prev).length;
                const expected = chars[nextIndex];
                if (expected !== undefined && ch !== expected) {
                    setErrors((n) => n + 1);
                }
                return prev + ch;
            });
        },
        [finished, language, chars]
    );

    const handleKeyUp = useCallback(() => setActiveCode(null), []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.focus();
        el.addEventListener("keydown", handleKeyDown);
        el.addEventListener("keyup", handleKeyUp);
        return () => {
            el.removeEventListener("keydown", handleKeyDown);
            el.removeEventListener("keyup", handleKeyUp);
        };
    }, [handleKeyDown, handleKeyUp]);

    // Ticker
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, []);

    // Auto-complete when text fully typed
    useEffect(() => {
        if (!finished && typedChars.length >= chars.length && chars.length > 0) {
            complete(true);
        }
    }, [typedChars.length, chars.length, finished, complete]);

    // Timed test completion
    useEffect(() => {
        if (!durationSeconds || !startedAt || finished) return;
        const elapsed = (now - startedAt) / 1000;
        if (elapsed >= durationSeconds) complete(true);
    }, [now, startedAt, durationSeconds, finished, complete]);

    const elapsed = startedAt ? (now - startedAt) / 1000 : 0;
    const remaining = durationSeconds ? Math.max(0, durationSeconds - elapsed) : null;
    const correctCount = typedChars.filter((c, i) => c === chars[i]).length;
    const wpm = elapsed > 0 ? correctCount / 5 / (elapsed / 60) : 0;
    const accuracy = typedChars.length > 0 ? (correctCount / typedChars.length) * 100 : 100;

    const reset = () => {
        setTyped("");
        setErrors(0);
        setStartedAt(null);
        setFinished(false);
        setActiveCode(null);
        containerRef.current?.focus();
    };

    const nextChar = chars[typedChars.length];

    return (
        <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="WPM" value={wpm.toFixed(0)} testid="typing-wpm-counter" />
                <StatCard
                    label="Accuracy"
                    value={`${accuracy.toFixed(0)}%`}
                    testid="typing-accuracy-counter"
                />
                <StatCard
                    label={durationSeconds ? "Time Left" : "Time"}
                    value={
                        durationSeconds
                            ? `${remaining.toFixed(0)}s`
                            : `${elapsed.toFixed(0)}s`
                    }
                    testid="typing-timer-counter"
                />
                <StatCard label="Errors" value={errors} testid="typing-errors-counter" />
            </div>

            {/* Text display + hidden focus target */}
            <div
                ref={containerRef}
                tabIndex={0}
                className="glass rounded-2xl p-6 sm:p-10 outline-none focus:ring-2 focus:ring-[var(--lt-terracotta)]/50 cursor-text"
                data-testid="typing-input-display"
                onClick={() => containerRef.current?.focus()}
            >
                <p
                    className={`${
                        language === "nepali" ? "font-nepali" : "font-mono-stat"
                    } text-2xl sm:text-3xl leading-relaxed tracking-wide break-words`}
                >
                    {chars.map((c, i) => {
                        let cls = "char char-pending";
                        if (i < typedChars.length) {
                            cls =
                                typedChars[i] === c
                                    ? "char char-correct"
                                    : "char char-wrong";
                        } else if (i === typedChars.length) {
                            cls = "char char-current";
                        }
                        return (
                            <span key={i} className={cls}>
                                {c === " " ? "\u00A0" : c}
                            </span>
                        );
                    })}
                </p>
                {!startedAt && !finished && (
                    <p className="mt-4 text-sm text-[var(--lt-muted)]">
                        Click here and start typing to begin.
                    </p>
                )}
            </div>

            <div className="flex items-center justify-between">
                <p className="text-xs text-[var(--lt-muted)] eyebrow">
                    {language === "nepali" ? "Traditional Nepali" : "English (QWERTY)"} · {mode}
                </p>
                <button
                    onClick={reset}
                    className="btn-ghost-line text-sm inline-flex items-center gap-2"
                    data-testid="typing-restart-btn"
                >
                    <RotateCcw size={14} /> Restart
                </button>
            </div>

            <Keyboard language={language} activeCode={activeCode} nextChar={nextChar} />
        </div>
    );
}

function StatCard({ label, value, testid }) {
    return (
        <div className="glass rounded-xl p-4" data-testid={testid}>
            <div className="eyebrow">{label}</div>
            <div className="font-mono-stat text-3xl mt-1 text-[var(--lt-text)]">{value}</div>
        </div>
    );
}
